/**
 * The proposal, opened up.
 *
 * A plan is never accepted from its summary. Every operation is listed with the
 * control it moves, the value before, the value after and the reason, and each
 * one can be refused on its own. Preview applies the ticked operations to the
 * live image without writing history, so the decision is made by looking at the
 * photograph rather than at a list of numbers.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type ChatMessage,
  type EditPlan,
  type OperationGroup,
  type Recipe,
  applyPlan,
  cloneRecipe,
  describePlan,
  isNeutral,
} from '@lumos/core';
import { useStore } from '../state/store.ts';
import { Tip } from './controls/Tooltip.tsx';

const GROUP_ORDER: readonly OperationGroup[] = ['light', 'color', 'subject', 'composition', 'look'];

const GROUP_TITLE: Record<OperationGroup, string> = {
  light: 'Light',
  color: 'Colour',
  subject: 'Subject',
  composition: 'Composition',
  look: 'Look',
};

/** Longest protected list shown in full before it is cut down to one line. */
const PROTECTED_LINE = 64;

interface PlanReviewProps {
  photoId: string;
  plan: EditPlan;
  disposition?: ChatMessage['disposition'];
}

export function PlanReview({ photoId, plan, disposition }: PlanReviewProps) {
  const pendingPlan = useStore((state) => state.pendingPlan);
  const recipe = useStore(
    (state) => state.photos.find((photo) => photo.id === photoId)?.recipe ?? null,
  );
  const togglePlanOperation = useStore((state) => state.togglePlanOperation);
  const setPlanSelection = useStore((state) => state.setPlanSelection);
  const acceptPlan = useStore((state) => state.acceptPlan);
  const dismissPlan = useStore((state) => state.dismissPlan);
  const stagePlan = useStore((state) => state.stagePlan);

  // Identity, not equality: the store resolves a plan by the same reference.
  const staged =
    pendingPlan !== null && pendingPlan.plan === plan && pendingPlan.photoId === photoId;
  const selected = staged ? pendingPlan.selected : null;

  const baseRecipe = useRef<Recipe | null>(null);
  const photoIdRef = useRef(photoId);
  photoIdRef.current = photoId;
  const [previewing, setPreviewing] = useState(false);

  const stopPreview = useCallback(() => {
    const base = baseRecipe.current;
    baseRecipe.current = null;
    setPreviewing(false);
    if (base) writeRecipeTo(photoIdRef.current, base);
  }, []);

  const startPreview = useCallback(() => {
    const photo = useStore.getState().photos.find((entry) => entry.id === photoIdRef.current);
    if (!photo) return;
    baseRecipe.current = cloneRecipe(photo.recipe);
    setPreviewing(true);
  }, []);

  // The preview follows the checkboxes: unticking an operation must take it
  // back out of the image immediately.
  useEffect(() => {
    const base = baseRecipe.current;
    if (!previewing || !base) return;
    writeRecipeTo(photoId, applyPlan(base, plan, selected));
  }, [previewing, selected, plan, photoId]);

  // The plan can stop being the staged one without this component unmounting,
  // because selecting another photo clears it. Never leave the image previewing.
  useEffect(() => {
    if (!staged && baseRecipe.current) stopPreview();
  }, [staged, stopPreview]);

  useEffect(
    () => () => {
      const base = baseRecipe.current;
      baseRecipe.current = null;
      if (base) writeRecipeTo(photoIdRef.current, base);
    },
    [],
  );

  // While previewing, "before" has to mean the recipe as it was before the
  // preview, or every row would read as no change at all.
  const reference = previewing && baseRecipe.current ? baseRecipe.current : recipe;

  // The camera's own white balance is what "as shot" means on this frame, so
  // the before-column has to read from it rather than the registry neutral.
  const asShot = useStore(
    (state) => state.photos.find((entry) => entry.id === photoId)?.image?.asShot,
  );
  const changes = useMemo(
    () =>
      reference
        ? describePlan(reference, plan, {
            asShot: asShot ? { kelvin: asShot.kelvin, tint: asShot.tint } : undefined,
          })
        : [],
    [reference, plan, asShot],
  );

  const grouped = useMemo(() => {
    const rows = changes.map((change, index) => ({ change, index }));
    return GROUP_ORDER.map((group) => ({
      group,
      rows: rows.filter((row) => row.change.group === group),
    })).filter((entry) => entry.rows.length > 0);
  }, [changes]);

  const total = plan.operations.length;
  const confidence = Math.round(plan.confidence * 100);
  const resolved = disposition === 'applied' || disposition === 'partial' || disposition === 'dismissed';
  const interactive = staged && total > 0;

  const untouched = plan.protected.join(', ');
  const untouchedShort =
    untouched.length > PROTECTED_LINE ? `${untouched.slice(0, PROTECTED_LINE).trimEnd()}…` : untouched;

  const head = (
    <div className="plan-head">
      <div className="plan-summary">{plan.summary}</div>
      {plan.borrowedTraits.length > 0 ? (
        <div className="plan-traits">
          <span className="label">Borrowed</span>
          {plan.borrowedTraits.map((trait) => (
            <span className="trait" key={trait}>
              {trait}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );

  const notes = (
    <div className="plan-warnings">
      {plan.warnings.map((warning, index) => (
        <div className={`warning${warning.severity === 'caution' ? ' caution' : ''}`} key={index}>
          <span>{warning.message}</span>
        </div>
      ))}

      {plan.protected.length > 0 ? (
        <div className="warning">
          {untouched === untouchedShort ? (
            <span>Left untouched: {untouched}</span>
          ) : (
            <Tip title="Left untouched" body={untouched}>
              <span>Left untouched: {untouchedShort}</span>
            </Tip>
          )}
        </div>
      ) : null}

      <div className="warning">
        <Tip
          title="Confidence"
          body="The model’s own estimate. It does not measure the result."
        >
          <span>Confidence {confidence}%</span>
        </Tip>
      </div>
    </div>
  );

  if (resolved) {
    return (
      <div className="plan">
        {head}
        <div className="plan-warnings">
          <div className="warning">
            <span>{outcomeLine(disposition, total)}</span>
          </div>
          {changes.length > 0 ? (
            <div className="warning">
              <span>
                Proposed: {changes.map((change) => `${change.label} ${change.after}`).join(' · ')}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="plan">
      {head}

      {total > 0 ? (
        <>
          {interactive ? (
            <div className="plan-group chat-actions">
              <span>
                {selected ? selected.size : 0} of {total} selected
              </span>
              <span className="topbar-spacer" />
              <button
                type="button"
                className="link-btn"
                onClick={() => setPlanSelection(new Set(plan.operations.map((_, index) => index)))}
              >
                All
              </button>
              <button type="button" className="link-btn" onClick={() => setPlanSelection(new Set())}>
                None
              </button>
            </div>
          ) : null}

          {grouped.map((entry) => (
            <div key={entry.group}>
              <div className="plan-group">{GROUP_TITLE[entry.group]}</div>
              {entry.rows.map(({ change, index }) => {
                const on = selected?.has(index) ?? false;
                const body = (
                  <div className="change-main">
                    <div className="change-title">
                      <span>{change.label}</span>
                      <span className="change-delta">
                        {change.before} → <b>{change.after}</b>
                        {change.detail ? ` · ${change.detail}` : ''}
                      </span>
                    </div>
                    <div className="change-reason">{change.reason}</div>
                  </div>
                );

                if (!interactive) {
                  return (
                    <div className="change" style={{ cursor: 'default' }} key={index}>
                      {body}
                    </div>
                  );
                }

                return (
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    aria-label={`${change.label}, ${change.before} to ${change.after}. ${change.reason}`}
                    className={`change${on ? ' on' : ''}`}
                    // A button does not stretch or left-align on its own.
                    style={{ width: '100%', textAlign: 'left' }}
                    key={index}
                    onClick={() => togglePlanOperation(index)}
                  >
                    <span className="change-check" aria-hidden="true">
                      {on ? '✓' : ''}
                    </span>
                    {body}
                  </button>
                );
              })}
            </div>
          ))}
        </>
      ) : null}

      {notes}

      {interactive ? (
        <div className="plan-foot">
          <button
            type="button"
            className="btn ember"
            disabled={!selected || selected.size === 0}
            onClick={() => {
              // Accept has to start from the recipe the plan was measured
              // against, so the preview is unwound first.
              stopPreview();
              acceptPlan();
            }}
          >
            Apply selected
          </button>
          <button
            type="button"
            className="btn quiet"
            onClick={() => {
              stopPreview();
              dismissPlan();
            }}
          >
            Dismiss
          </button>
          <span className="topbar-spacer" />
          <button
            type="button"
            className={`btn${previewing ? ' active' : ''}`}
            aria-pressed={previewing}
            disabled={!previewing && (!selected || selected.size === 0)}
            onClick={() => (previewing ? stopPreview() : startPreview())}
          >
            {previewing ? 'Previewing' : 'Preview'}
          </button>
        </div>
      ) : null}

      {!staged && total > 0 ? (
        <div className="plan-foot">
          <button type="button" className="btn" onClick={() => stagePlan(photoId, plan)}>
            Review these changes
          </button>
          <span className="muted">Nothing applied yet.</span>
        </div>
      ) : null}
    </div>
  );
}

function outcomeLine(disposition: ChatMessage['disposition'], total: number): string {
  const changes = `${total} change${total === 1 ? '' : 's'}`;
  if (disposition === 'applied') return `Applied all ${changes}.`;
  if (disposition === 'partial') return `Applied some of the ${changes}.`;
  return 'Dismissed. Nothing was applied.';
}

/**
 * Writes a recipe onto one photo without touching its history.
 *
 * A preview must be invisible to undo, and it must land on the photo the plan
 * belongs to even if the selection has moved on, so it takes the store's own
 * path when that photo is selected and goes direct when it is not.
 */
function writeRecipeTo(photoId: string, recipe: Recipe): void {
  const state = useStore.getState();
  if (state.selectedId === photoId) {
    state.setRecipe(recipe);
    return;
  }
  useStore.setState({
    photos: state.photos.map((photo) =>
      photo.id === photoId ? { ...photo, recipe, edited: !isNeutral(recipe) } : photo,
    ),
  });
}

export default PlanReview;
